import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContextAWS';
import { useTestDataBanner } from '../contexts/TestDataBannerContext';
import leagueApiService from '../services/leagueApi';
import draftService from '../services/draftService';
import { USE_ROSTER_FIXTURES } from '../services/rosterFixtures';
import { enrichLeagueWithFixtures } from '../services/leagueFixtures';
import TeamSettingsModal from './TeamSettingsModal';
import TeamLogo from './TeamLogo';
import { 
  TrophyIcon, 
  ChevronDownIcon,
  UsersIcon,
  CogIcon,
  HistoryIcon
} from './icons/StreamlineFlexSolid';
import { buildMatchupsWithResults, getScoringTypeInfo, computeStandings } from '../utils/standingsUtils';
import { getCurrentDate } from '../utils/rosterManager';

function LeagueLayout() {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // Season-phase test-data mode (pre-draft / mid-season / after-season).
  const { mode: phase } = useTestDataBanner();
  const [league, setLeague] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSticky, setIsSticky] = useState(false);
  const [selectedOpponent, setSelectedOpponent] = useState(null);
  const [showTeamsMenu, setShowTeamsMenu] = useState(false);
  const [showCommissionerMenu, setShowCommissionerMenu] = useState(false);
  const [showOtherMenu, setShowOtherMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showTeamSettings, setShowTeamSettings] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  // How many leading tabs fit inline; the rest collapse into the "More" menu.
  // Starts high so all tabs render first, then the measurement effect trims it.
  const [visibleTabCount, setVisibleTabCount] = useState(99);
  const tabsWrapRef = useRef(null);
  const tabMeasureRef = useRef(null);
  const moreMenuRef = useRef(null);
  const [teamSettingsNotification, setTeamSettingsNotification] = useState(null);
  const [draft, setDraft] = useState(null);
  const subheaderRef = useRef(null);
  const teamsMenuRef = useRef(null);
  const commissionerMenuRef = useRef(null);
  const otherMenuRef = useRef(null);

  useEffect(() => {
    loadLeague();
  }, [leagueId]);

  useEffect(() => {
    if (!leagueId) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await draftService.getDraft(leagueId);
        if (!cancelled) setDraft(d);
      } catch {
        if (!cancelled) setDraft(null);
      }
    })();
    return () => { cancelled = true; };
  }, [leagueId]);

  useEffect(() => {
    if (!leagueId) return;
    const sub = draftService.subscribeToDraftUpdates(leagueId, (updated) => {
      setDraft(updated);
    });
    return () => { sub?.unsubscribe?.(); };
  }, [leagueId]);

  // Track window width for responsive navigation
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load league data and normalize fields
  const loadLeague = async () => {
    if (!leagueId) return;
    setLoading(true);
    try {
      const res = await leagueApiService.getLeague(leagueId);
      const leagueData = res?.league || res;

      if (leagueData) {
        let settings = leagueData.settings || {};
        try { settings = typeof settings === 'string' ? JSON.parse(settings) : settings; } catch { settings = {}; }

        let season = leagueData.season || {};
        try { season = typeof season === 'string' ? JSON.parse(season) : season; } catch { season = {}; }

        let teams = leagueData.teams || [];
        try { teams = typeof teams === 'string' ? JSON.parse(teams) : teams; } catch { teams = []; }

        let built = {
          id: leagueData.leagueId || leagueData.id,
          leagueId: leagueData.leagueId || leagueData.id,
          name: leagueData.name,
          description: leagueData.description,
          creatorId: leagueData.creatorId,
          settings,
          season,
          seasonYear: season?.year || new Date().getFullYear(),
          draftDate: leagueData.draftDate,
          draftTime: leagueData.draftTime,
          isPrivate: leagueData.isPrivate,
          status: leagueData.status || 'Draft',
          teams,
          createdAt: leagueData.createdAt,
          updatedAt: leagueData.updatedAt
        };
        // Fixtures mode: overlay a full sample league (10 teams, H2H scoring,
        // history) so standings, live matchup, scoreboard and history all render
        // from local data.
        if (USE_ROSTER_FIXTURES) {
          const uid = user?.userId || user?.id || user?.sub;
          built = enrichLeagueWithFixtures(built, uid, user);
        }
        setLeague(built);
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Error loading league:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  // Handle scroll to detect when subheader is sticky
  useEffect(() => {
    const handleScroll = () => {
      if (subheaderRef.current) {
        const rect = subheaderRef.current.getBoundingClientRect();
        setIsSticky(rect.top <= 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Set default opponent when league loads
  useEffect(() => {
    if (league && league.teams && Array.isArray(league.teams) && !selectedOpponent) {
      const userId = user?.userId || user?.id || user?.sub;
      const userTeam = league.teams.find(team => team.owner === userId) || league.teams[0];
      const userTeamId = userTeam?.id ?? userTeam?.teamId;
      const opponents = league.teams.filter(team => {
        if (team.owner === userId) return false;
        const tid = team?.id ?? team?.teamId;
        return !(tid != null && userTeamId != null && tid === userTeamId);
      });
      if (opponents.length > 0) setSelectedOpponent(opponents[0]);
    }
  }, [league, user, selectedOpponent]);

  // Close Teams and Other menus when clicking outside
  useEffect(() => {
    if (!showTeamsMenu && !showOtherMenu) return;
    const handleClickOutside = (e) => {
      const teamsEl = teamsMenuRef.current;
      const otherEl = otherMenuRef.current;
      if (showTeamsMenu && teamsEl && !teamsEl.contains(e.target)) setShowTeamsMenu(false);
      if (showOtherMenu && otherEl && !otherEl.contains(e.target)) setShowOtherMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTeamsMenu, showOtherMenu]);

  // Derive teams/userTeam/userTeamId before any returns (needed for hooks)
  const teams = useMemo(() => {
    if (!league) return [];
    const raw = league.teams;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return [];
      }
    }
    return [];
  }, [league]);

  const getTeamNameHelper = (team, index) => {
    if (team.name && team.name.trim() !== '') return team.name;
    return `Team ${index + 1}`;
  };

  const teamsWithNames = useMemo(() =>
    teams.map((team, index) => ({ ...team, name: getTeamNameHelper(team, index) })),
  [teams]);

  const userId = user?.userId || user?.id || user?.sub;
  const userTeam = teamsWithNames.find(team => team.owner === userId) || teamsWithNames[0];
  const userTeamId = userTeam?.id ?? userTeam?.teamId;

  const opponentTeams = useMemo(() => {
    return teamsWithNames.filter(team => {
      if (team.owner === userId) return false;
      const tid = team?.id ?? team?.teamId;
      if (tid != null && userTeamId != null && tid === userTeamId) return false;
      return true;
    });
  }, [teamsWithNames, userId, userTeamId]);

  // Current week and matchup for H2H leagues (for roster page team card) - hooks must run unconditionally
  const { isHeadToHead } = useMemo(() => getScoringTypeInfo(league || {}), [league]);
  const currentWeek = useMemo(() => {
    if (!league) return 1;
    const today = new Date(getCurrentDate() + 'T00:00:00');
    const seasonStart = league?.season?.startDate ? new Date(league.season.startDate + 'T00:00:00') : null;
    const seasonEnd = league?.season?.endDate ? new Date(league.season.endDate + 'T00:00:00') : null;
    if (!seasonStart || today < seasonStart) return 1;
    if (seasonEnd && today > seasonEnd) {
      const stored = league?.settings?.schedule?.matchups || [];
      return stored.length > 0 ? Math.max(...stored.map((m) => m.week || m.period || 1)) : 1;
    }
    return 1;
  }, [league?.season?.startDate, league?.season?.endDate, league?.settings?.schedule?.matchups]);

  const weekMatchups = useMemo(() => {
    if (!league || !isHeadToHead) return [];
    const allMatchups = buildMatchupsWithResults(league);
    const getTeamById = (id) => teams.find((t) => t.id === id || t.teamId === id);
    const getDisplayName = (t) => (t?.name && String(t.name).trim() ? t.name : 'Team');
    
    // Get categories for the league
    const batting = league?.settings?.battingCategories;
    const pitching = league?.settings?.pitchingCategories;
    let categories = [];
    if (Array.isArray(batting) && Array.isArray(pitching)) {
      const battingCats = batting.map((c) => ({
        key: c.key || c,
        label: c.label || c.key || c,
        higherWins: !(c.isAverage || (c.key && ['AVG', 'OBP', 'SLG', 'OPS'].includes(c.key))),
      }));
      const pitchingCats = pitching.map((c) => ({
        key: c.key || c,
        label: c.label || c.key || c,
        higherWins: !(c.key && ['ERA', 'WHIP'].includes(c.key)),
      }));
      categories = [...battingCats, ...pitchingCats];
    } else {
      categories = [
        { key: 'R', label: 'R', higherWins: true },
        { key: 'HR', label: 'HR', higherWins: true },
        { key: 'RBI', label: 'RBI', higherWins: true },
        { key: 'SB', label: 'SB', higherWins: true },
        { key: 'AVG', label: 'AVG', higherWins: true },
        { key: 'W', label: 'W', higherWins: true },
        { key: 'SV', label: 'SV', higherWins: true },
        { key: 'K', label: 'K', higherWins: true },
        { key: 'ERA', label: 'ERA', higherWins: false },
        { key: 'WHIP', label: 'WHIP', higherWins: false },
      ];
    }
    
    const list = allMatchups
      .filter((m) => m.week === currentWeek)
      .map((m) => {
        const t1 = getTeamById(m.team1Id);
        const t2 = getTeamById(m.team2Id);
        if (!t1 || !t2) return null;
        const team1Wins = m.team1FullWins ?? 0;
        const team2Wins = m.team2FullWins ?? 0;
        const totalCategories = m.totalCategories ?? 10;
        const ties = totalCategories - team1Wins - team2Wins;
        
        // Generate mock category results using deterministic seed
        const i1 = teams.findIndex((t) => t.id === t1?.id || t.teamId === t1?.teamId);
        const i2 = teams.findIndex((t) => t.id === t2?.id || t.teamId === t2?.teamId);
        const baseSeed = (i1 + 1) * 7 + (i2 + 1) * 11 + currentWeek * 13;
        
        const categoryResults = categories.map((cat, idx) => {
          const seed = baseSeed + idx * 17;
          const rand = Math.abs(Math.sin(seed) * 10000) % 100;
          
          // Generate mock values based on category type
          let team1Value, team2Value;
          if (cat.key === 'AVG' || cat.key === 'OBP' || cat.key === 'SLG' || cat.key === 'OPS') {
            team1Value = (0.200 + (rand / 100) * 0.150).toFixed(3);
            team2Value = (0.200 + ((100 - rand) / 100) * 0.150).toFixed(3);
          } else if (cat.key === 'ERA') {
            team1Value = (2.50 + (rand / 100) * 3.00).toFixed(2);
            team2Value = (2.50 + ((100 - rand) / 100) * 3.00).toFixed(2);
          } else if (cat.key === 'WHIP') {
            team1Value = (1.00 + (rand / 100) * 0.50).toFixed(2);
            team2Value = (1.00 + ((100 - rand) / 100) * 0.50).toFixed(2);
          } else {
            team1Value = Math.floor(20 + (rand / 100) * 30);
            team2Value = Math.floor(20 + ((100 - rand) / 100) * 30);
          }
          
          // Determine winner
          let winner = 'tie';
          if (cat.higherWins) {
            if (parseFloat(team1Value) > parseFloat(team2Value)) winner = 'team1';
            else if (parseFloat(team2Value) > parseFloat(team1Value)) winner = 'team2';
          } else {
            if (parseFloat(team1Value) < parseFloat(team2Value)) winner = 'team1';
            else if (parseFloat(team2Value) < parseFloat(team1Value)) winner = 'team2';
          }
          
          return {
            category: cat,
            team1Value,
            team2Value,
            winner,
          };
        });
        
        return {
          team1: { ...t1, name: getDisplayName(t1) },
          team2: { ...t2, name: getDisplayName(t2) },
          team1Wins,
          team2Wins,
          ties: Math.max(0, ties),
          totalCategories,
          categoryResults,
        };
      })
      .filter(Boolean);
    return list;
  }, [league, isHeadToHead, currentWeek, teams]);

  // The logged-in user's matchup (used by "My Roster"); team pages pick their own
  // out of the same weekMatchups list.
  const currentMatchup = useMemo(
    () => weekMatchups.find((m) => (m.team1.id || m.team1.teamId) === userTeamId || (m.team2.id || m.team2.teamId) === userTeamId) || null,
    [weekMatchups, userTeamId],
  );

  // Clear team settings notification after delay
  useEffect(() => {
    if (!teamSettingsNotification) return;
    const t = setTimeout(() => setTeamSettingsNotification(null), 2500);
    return () => clearTimeout(t);
  }, [teamSettingsNotification]);

  // User team's season record and place in standings (for roster page team card)
  const userTeamStanding = useMemo(() => {
    if (!league) return null;
    const { standings, isHeadToHead, isRotisserie, isPointsBased } = computeStandings(league);
    if (!standings?.length) return null;
    const idx = standings.findIndex(
      (s) => (s.id || s.teamId) === userTeamId || s.owner === userId
    );
    if (idx === -1) return null;
    const entry = standings[idx];
    return {
      rank: idx + 1,
      totalTeams: standings.length,
      wins: entry.wins ?? 0,
      losses: entry.losses ?? 0,
      ties: entry.ties ?? 0,
      rotoPoints: entry.rotoPoints,
      points: entry.points,
      isHeadToHead,
      isRotisserie,
      isPointsBased,
    };
  }, [league, userTeamId, userId]);

  // Check if user is commissioner
  const isCommissioner = league?.creatorId === (user?.userId || user?.id || user?.sub);

  // The draft only matters before the season starts. In pre-draft show a "Draft"
  // tab; once drafted (mid-season / after-season) hide it entirely.
  const showDraftTab = phase === 'pre-draft';
  const allTabs = [
    { id: 'roster', label: 'My Roster', path: `/league/${leagueId}/roster` },
    { id: 'players', label: 'Players', path: `/league/${leagueId}/players` },
    { id: 'farm', label: 'Farm', path: `/league/${leagueId}/farm` },
    ...(showDraftTab ? [{ id: 'draft', label: 'Draft', path: `/league/${leagueId}/draft` }] : []),
    { id: 'scoreboard', label: 'Live Matchup', path: `/league/${leagueId}/scoreboard` },
    { id: 'standings', label: 'Standings', path: `/league/${leagueId}/standings` },
    { id: 'history', label: 'History', path: `/league/${leagueId}/history` }
  ];
  
  // Responsive tab overflow: measure the tabs against the space actually
  // available and only collapse trailing tabs into "More" when they don't fit
  // (rather than hiding them at fixed width breakpoints). The hidden measurer
  // below renders every tab plus a "More" button at natural size so we can read
  // each one's width without disturbing the visible layout.
  useLayoutEffect(() => {
    const wrap = tabsWrapRef.current;
    const measure = tabMeasureRef.current;
    if (!wrap || !measure) return undefined;

    const GAP = 2; // matches .league-segmented-control gap

    const recompute = () => {
      const btns = measure.querySelectorAll('.segment-button');
      if (btns.length === 0) return;
      const moreWidth = btns[btns.length - 1].offsetWidth;
      const tabWidths = Array.from(btns)
        .slice(0, allTabs.length)
        .map((b) => b.offsetWidth);
      const avail = wrap.clientWidth;
      // Bail on a not-yet-laid-out container so we don't collapse to 1 tab off a 0 width.
      if (!avail) return;

      // Total width of all tabs with no "More" button.
      const totalNoMore = tabWidths.reduce((sum, w, i) => sum + w + (i > 0 ? GAP : 0), 0);

      let count;
      if (totalNoMore <= avail) {
        // Everything fits — show every tab, no "More" button.
        count = allTabs.length;
      } else {
        // Reserve room for the More button, then fit as many leading tabs as possible.
        let used = 0;
        count = 0;
        for (let i = 0; i < tabWidths.length; i++) {
          const w = tabWidths[i] + (i > 0 ? GAP : 0);
          if (used + w + GAP + moreWidth <= avail) {
            used += w;
            count += 1;
          } else {
            break;
          }
        }
        count = Math.max(1, count);
      }
      setVisibleTabCount((prev) => (prev === count ? prev : count));
    };

    recompute();

    const ro = new ResizeObserver(recompute);
    ro.observe(wrap);
    // Re-measure once web fonts settle, since label widths change with the font.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(recompute).catch(() => {});
    }
    return () => ro.disconnect();
    // `league` is included so the measurement re-runs once the sub-header
    // actually mounts (the refs are null during the loading render).
  }, [allTabs.length, showDraftTab, windowWidth, league]);

  const safeVisibleCount = Math.min(visibleTabCount, allTabs.length);
  const visibleTabs = allTabs.slice(0, safeVisibleCount);
  const overflowTabs = allTabs.slice(safeVisibleCount);

  if (loading) {
    return <div className="container">Loading...</div>;
  }

  if (!league) {
    return <div className="container">League not found</div>;
  }

  if (!userTeam) {
    return <div className="container">No team found for this league</div>;
  }

  return (
    <div>
      {/* League Subheader - below GamesBanner: white bg, league name + year + tabs left; Teams + Commissioner right */}
      <div
        ref={subheaderRef}
        className={`league-subheader ${isSticky ? 'sticky' : ''}`}
      >
        <div className="league-subheader-inner">
          {/* Left: League name, year, navigation - left justified */}
          <div className="league-subheader-left">
            <h2
              className="league-subheader-title"
              style={{
                maxWidth: windowWidth < 768 ? '120px' : windowWidth < 1024 ? '180px' : 'none',
                flexShrink: windowWidth < 768 ? 1 : 0
              }}
            >
              {league.name}
            </h2>
            {windowWidth >= 640 && (
              <span className="league-subheader-season-year">
                {league.seasonYear || '2026'}
              </span>
            )}
            <div className="league-tabs league-subheader-tabs" ref={tabsWrapRef}>
              {/* Hidden measurer: every tab + a More button at natural size, used
                  to decide how many tabs fit before collapsing into More. */}
              <div className="league-segmented-control league-tabs-measure" ref={tabMeasureRef} aria-hidden="true">
                {allTabs.map(tab => (
                  <button key={tab.id} type="button" className="segment-button" tabIndex={-1}>
                    {tab.label}
                  </button>
                ))}
                <button type="button" className="segment-button segment-button-more" tabIndex={-1}>
                  More
                  <ChevronDownIcon size={16} />
                </button>
              </div>
              <div className="league-segmented-control" style={{ flexShrink: 0 }}>
                {visibleTabs.map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`segment-button ${location.pathname === tab.path ? 'active' : ''}`}
                    onClick={() => navigate(tab.path)}
                  >
                    {tab.label}
                  </button>
                ))}
                {overflowTabs.length > 0 && (
                  <div
                      ref={moreMenuRef}
                      onMouseEnter={() => setShowMoreMenu(true)}
                      onMouseLeave={() => setShowMoreMenu(false)}
                      className="more-menu-wrapper"
                    >
                    <button
                      type="button"
                      className={`segment-button segment-button-more ${overflowTabs.some(t => location.pathname === t.path) ? 'active' : ''}`}
                        style={{
                          backgroundColor: showMoreMenu ? 'var(--apple-background-secondary)' : undefined,
                          boxShadow: showMoreMenu ? '0 1px 3px rgba(0,0,0,0.08)' : undefined
                        }}
                    >
                      More
                      <ChevronDownIcon
                        size={16}
                        style={{
                          transform: showMoreMenu ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s'
                        }}
                      />
                    </button>
                    {showMoreMenu && (
                  <div className="more-menu-dropdown">
                      {/* Overflow tabs */}
                      {overflowTabs.map(tab => (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => {
                            navigate(tab.path);
                            setShowMoreMenu(false);
                          }}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: 'none',
                            backgroundColor: location.pathname === tab.path ? '#e7f3ff' : 'transparent',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontSize: '14px',
                            color: '#333',
                            transition: 'background-color 0.15s'
                          }}
                          onMouseEnter={(e) => { if (location.pathname !== tab.path) e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = location.pathname === tab.path ? '#e7f3ff' : 'transparent'; }}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Teams menu, Commissioner - right justified */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, marginLeft: 'auto' }}>
            {opponentTeams.length > 0 && windowWidth >= 480 && (
              <div
                ref={teamsMenuRef}
                onMouseEnter={() => setShowTeamsMenu(true)}
                onMouseLeave={() => setShowTeamsMenu(false)}
                style={{ display: 'inline-flex', position: 'relative' }}
              >
                <button
                  type="button"
                  className="tab-button"
                  style={{
                    backgroundColor: showTeamsMenu ? '#f8f9fa' : 'transparent',
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  {windowWidth >= 640 ? 'Teams' : <UsersIcon size={16} />}
                  <ChevronDownIcon
                    size={16}
                    style={{
                      transform: showTeamsMenu ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s'
                    }}
                  />
                </button>
                {showTeamsMenu && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: -2,
                    paddingTop: 2,
                    backgroundColor: 'white',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '200px',
                    zIndex: 1001,
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}>
                    {opponentTeams.map((team, idx) => {
                      const tid = team?.id ?? team?.teamId;
                      return (
                        <button
                          key={tid ?? `opponent-${idx}`}
                          type="button"
                          onClick={() => {
                            if (tid) navigate(`/league/${leagueId}/team/${tid}/roster`);
                            setShowTeamsMenu(false);
                          }}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: 'none',
                            backgroundColor: 'transparent',
                            cursor: tid ? 'pointer' : 'default',
                            textAlign: 'left',
                            fontSize: '14px',
                            color: '#333',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'background-color 0.15s'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                          disabled={!tid}
                        >
                          <TeamLogo team={team} size={28} />
                          <span>{team.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {isCommissioner && (
              <div
                ref={commissionerMenuRef}
                onMouseEnter={() => setShowCommissionerMenu(true)}
                onMouseLeave={() => setShowCommissionerMenu(false)}
                style={{ display: 'inline-flex', position: 'relative' }}
              >
                <button
                  type="button"
                  className="tab-button"
                  title="Commissioner"
                  style={{
                    backgroundColor: showCommissionerMenu ? '#f8f9fa' : 'transparent',
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  {windowWidth >= 640 ? 'Commissioner' : null}
                  <ChevronDownIcon
                    size={16}
                    style={{
                      transform: showCommissionerMenu ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s'
                    }}
                  />
                </button>
                {showCommissionerMenu && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: -2,
                    paddingTop: 2,
                    backgroundColor: 'white',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '180px',
                    zIndex: 1001
                  }}>
                    <button
                      type="button"
                      onClick={() => {
                        navigate(`/league/${leagueId}/commissioner`);
                        setShowCommissionerMenu(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        backgroundColor: location.pathname === `/league/${leagueId}/commissioner` ? '#e7f3ff' : 'transparent',
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontSize: '14px',
                        color: '#333',
                        transition: 'background-color 0.15s'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = location.pathname === `/league/${leagueId}/commissioner` ? '#e7f3ff' : '#f8f9fa'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = location.pathname === `/league/${leagueId}/commissioner` ? '#e7f3ff' : 'transparent'; }}
                    >
                      Commissioner Hub
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        navigate(`/league/${leagueId}/settings`);
                        setShowCommissionerMenu(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        backgroundColor: location.pathname === `/league/${leagueId}/settings` ? '#e7f3ff' : 'transparent',
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontSize: '14px',
                        color: '#333',
                        transition: 'background-color 0.15s'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = location.pathname === `/league/${leagueId}/settings` ? '#e7f3ff' : '#f8f9fa'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = location.pathname === `/league/${leagueId}/settings` ? '#e7f3ff' : 'transparent'; }}
                    >
                      League settings
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tab Content - no max-width so roster + news can use full width. Padding
          comes from the .container class (responsive --spacing-* tokens). */}
      <div className="container" style={{ marginTop: isSticky ? '0' : '20px', maxWidth: 'none' }}>
        <Outlet context={{ league, setLeague, userTeam, selectedOpponent, currentMatchup, weekMatchups, userTeamStanding, onOpenTeamSettings: () => setShowTeamSettings(true) }} />
      </div>

      {/* Team Settings Modal */}
      {showTeamSettings && userTeam && (
        <TeamSettingsModal
          userTeam={userTeam}
          league={league}
          setLeague={setLeague}
          onClose={() => setShowTeamSettings(false)}
          onSuccess={(msg) => setTeamSettingsNotification({ type: 'success', message: msg })}
          onError={(msg) => setTeamSettingsNotification({ type: 'error', message: msg })}
        />
      )}

      {/* Team Settings Notification */}
      {teamSettingsNotification && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 3001,
            backgroundColor: teamSettingsNotification.type === 'success' ? '#d4edda' : '#f8d7da',
            color: teamSettingsNotification.type === 'success' ? '#155724' : '#721c24',
            border: `1px solid ${teamSettingsNotification.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
            borderRadius: '8px',
            padding: '12px 20px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {teamSettingsNotification.message}
        </div>
      )}
    </div>
  );
}

export default LeagueLayout;

